import type { ComponentType } from "react";
import DailySpinBanner from "../DailySpinBanner";
import SubscriptionBanner from "./SubscriptionBanner";
import TokensBanner from "./TokensBanner";
import UnderageVerificationBanner from "./UnderageVerificationBanner";
import WebcamBanner from "./WebcamBanner";
import WebcamBannerNative from "./WebcamBannerNative";

/** Every banner takes the picker's dismiss handler; variants that are not
    dismissible are simply never handed one. */
export type AdsBannerProps = { onDismiss?: () => void };

export type AdsBannerVariant =
  | "dailyspin"
  | "tokens"
  | "subscription"
  | "webcam"
  | "webcam_native"
  | "underage_verification";

export type AdsBannerConfig = {
  id: AdsBannerVariant;
  /** 2d contract: dismissal is defined here, not inherited — a dismissible
      variant hides for 24h under a per-variant key. underage_verification is
      the one gate and never dismisses. */
  dismissible?: boolean;
  Component: ComponentType<AdsBannerProps>;
  slot?: string;
  bannedFromPages?: string[];
  allowedPages?: string[];
  weight?: number;
  pagePriorities?: Record<string, number>;
  isAvailable?: (params: { user: any; pathname: string }) => boolean;
};

export const ADS_BANNERS: AdsBannerConfig[] = [
  {
    id: "underage_verification",
    Component: UnderageVerificationBanner,
    pagePriorities: {
      "*": 40,
    },
    isAvailable: ({ user }) => user?.informations?.verified_age !== true,
  },
  {
    id: "dailyspin",
    dismissible: true,
    Component: DailySpinBanner,
    pagePriorities: {
      "*": 30,
    },
    isAvailable: ({ user }) =>
      Boolean(user?.informations) &&
      user?.informations?.type !== "guest" &&
      user?.informations?.gender === "man" &&
      Boolean(user?.informations?.is_email_confirmed) &&
      user?.informations?.can_play_dailyspin !== false,
  },
  {
    id: "tokens",
    dismissible: true,
    Component: TokensBanner,
    bannedFromPages: ["/tokens"],
    pagePriorities: {
      "*": 20,
    },
  },
  {
    id: "subscription",
    dismissible: true,
    Component: SubscriptionBanner,
    pagePriorities: {
      "*": 20,
    },
    isAvailable: ({ user }) => {
      const gender = user?.informations?.gender;
      return (
        gender !== "woman" &&
        gender !== "couple" &&
        !user?.informations?.is_subscribed
      );
    },
  },
  {
    id: "webcam_native",
    dismissible: true,
    Component: WebcamBannerNative,
    pagePriorities: {
      "*": 20,
    },
  },
];

export const DEACTIVATED_ADS_BANNERS: AdsBannerConfig[] = [
  {
    id: "webcam",
    Component: WebcamBanner,
  },
];
